import React, {
 useCallback,
 useEffect,
 useMemo,
 useState,
} from 'react'
import {useForm} from 'react-hook-form'
// import {toast} from 'react-toastify'
import Spinner from 'layout/common/Spinner'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
// import {useAuth} from 'context/AuthProvider'
import {ColumnProps, IFormField} from 'models/Common'
import KTTable from 'helpers/ui-components/KTTable'
import {
 convertEpochToDate,
 formatNumberForDisplay,
} from 'utils/helper'
import {
 IAmountDetails,
 ICounterOrderPayload,
 DiscountType,
 IItemDetails,
 IOrderDetails,
 IOrderDiscountDetails,
} from 'models/OrderManagement'
import {
 downloadInvoice,
 fetchOrderDetails,
 previewOrderCounter,
 sendOrderForConfirmation,
} from 'utils/api/auth/orderManagementService'
import GenericFormBuilder from 'helpers/ui-components/GenericFormBuilder'
import {PageLink, PageTitle} from 'layout/master-layout'
import {toast} from 'react-toastify'
import {downloadFileFromUrl} from 'utils/api/httpService'
import {
 getTradeListPath,
 isValidTradeTab,
} from './tradeRoutes'
import AgreedPriceCell from './AgreedPriceCell'
import EditDiscountModal from './EditDiscountModal'
import NegotiationBanner from './NegotiationBanner'
import OrderTotalsSummary from './OrderTotalsSummary'
import {isNegotiatedOrder} from './tradeNegotiationUtils'
import {useNavigate} from 'react-router-dom'
import {USER_CATEGORY} from 'constants/userCategory'

const ORDER_STATUS_TO_TAB: Record<string, string> = {
 PENDING: 'open_orders',
 CONFIRMED: 'accepted_orders',
 SHIPPED: 'dispatched_orders',
 DELIVERED: 'dispatched_orders',
 PAYMENT_CONFIRMATION: 'pending_payment_orders',
 PAYMENT_CONFIRMED: 'pending_payment_orders',
 COMPLETE: 'completed_orders',
 REJECTED: 'rejected_cancelled_orders',
 CANCELLED: 'rejected_cancelled_orders',
}

const getAgreedPrice = (item: IItemDetails) =>
 item.agreed_price ?? item.price_unit

const getCataloguePrice = (item: IItemDetails) =>
 item.catalogue_price ?? item.price_unit

const normalizeItems = (items: IItemDetails[]) =>
 items.map((item) => ({
  ...item,
  agreed_price: getAgreedPrice(item),
  catalogue_price: getCataloguePrice(item),
 }))

type IProps = {
 selectedOrder: IFormField | null
 activeTabId: string
 handleActionBtnClick: (newState: IFormField | null) => void
 setSelectedOrder?: (id: IFormField | null) => void
}

const OrderDetails = (props: IProps) => {
 const {t} = useTranslation()
 const [loader, setLoader] = useState(false)
 const [itemList, setItemList] = useState<IItemDetails[]>(
  []
 )
 const [originalItemList, setOriginalItemList] = useState<
  IItemDetails[]
 >([])
 const navigate = useNavigate()
 const [editingItemId, setEditingItemId] = useState<
  number | null
 >(null)
 const [editingPriceDraft, setEditingPriceDraft] =
  useState('')
 const [showEditDiscountModal, setShowEditDiscountModal] =
  useState(false)
 const [discountType, setDiscountType] =
  useState<DiscountType>('FLAT')
 const [discountValue, setDiscountValue] = useState(0)
 const [discountAmount, setDiscountAmount] = useState(0)
 const [originalDiscountType, setOriginalDiscountType] =
  useState<DiscountType>('FLAT')
 const [originalDiscountValue, setOriginalDiscountValue] =
  useState(0)
 const [amountDetails, setAmountDetails] =
  useState<IAmountDetails | null>(null)
 const isEditing = editingItemId !== null

 const userAccess = localStorage.getItem('user')
 const userCategoryKey = userAccess
  ? JSON.parse(userAccess)?.userCategoryKey
  : null
 const isPMU = userCategoryKey === USER_CATEGORY.PMU_BAYER
 const isDistributorDirector =
  userCategoryKey === USER_CATEGORY.DISTRIBUTOR_DIRECTOR
 const [orderDetails, setOrderDetails] =
  useState<IOrderDetails | null>(null)
 const [actionsAllowed, setActionsAllowed] = useState<
  boolean | undefined
 >(props.selectedOrder?.actionsAllowed)

 const statusDerivedTabId = orderDetails?.order_status
  ? ORDER_STATUS_TO_TAB[orderDetails.order_status]
  : undefined

 const actionTabId = statusDerivedTabId || props.activeTabId

 const isOpenOrderTab = actionTabId === 'open_orders'
 const showDistributorNegotiationUI =
  isDistributorDirector && isOpenOrderTab
 const showOrderSummary =
  showDistributorNegotiationUI ||
  [
   'accepted_orders',
   'dispatched_orders',
   'pending_payment_orders',
   'completed_orders',
   'rejected_cancelled_orders',
  ].includes(actionTabId)

 const tradeListPath = isValidTradeTab(props.activeTabId)
  ? getTradeListPath(props.activeTabId)
  : getTradeListPath()

 const breadcrumbs: PageLink[] = [
  {
   title: t('breadCrumbs.home'),
   path: '/home-page',
   isActive: false,
  },
  {
   title: t('breadCrumbs.trade'),
   path: tradeListPath,
   isActive: false,
  },
  {
   title: props.selectedOrder?.orderId?.toString(),
   path: '',
   isActive: true,
  },
 ]

 const {
  register,
  unregister,
  control,
  getValues,
  watch,
  setValue,
  reset,
  formState: {errors},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
 })

 const formFields = [
  {
   label: t('orderManagement.orderDetails.orderId'),
   label_key: 'display_order_number',
   field_type: 'TEXT_FIELD',
   placeholder: t('orderManagement.orderDetails.orderId'),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.orderDate'),
   label_key: 'order_date',
   field_type: 'DATE_FIELD',
   placeholder: t('orderManagement.orderDetails.orderDate'),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.buyerName'),
   label_key: 'aggregator_name',
   field_type: 'TEXT_FIELD',
   placeholder: t('orderManagement.orderDetails.buyerName'),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.customerType'),
   label_key: 'customer_type',
   field_type: 'TEXT_FIELD',
   placeholder: t(
    'orderManagement.orderDetails.customerType'
   ),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.contact'),
   label_key: 'aggregator_number',
   field_type: 'TEXT_FIELD',
   placeholder: t('orderManagement.orderDetails.contact'),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.sellerName'),
   label_key: 'distributor_name',
   field_type: 'TEXT_FIELD',
   placeholder: t(
    'orderManagement.orderDetails.sellerName'
   ),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.orderValue'),
   label_key: 'order_total',
   field_type: 'CURRENCY_FIELD',
   placeholder: t(
    'orderManagement.orderDetails.orderValue'
   ),
   is_disabled: true,
  },
  {
   label: t('orderManagement.orderDetails.customerAddress'),
   label_key: 'complete_address',
   field_type: 'TEXTAREA_FIELD',
   colSpan: 2,
   placeholder: t(
    'orderManagement.orderDetails.customerAddress'
   ),
   is_disabled: true,
  },
 ]

 const computedOrderTotal = useMemo(
  () =>
   itemList.reduce(
    (sum, item) =>
     sum + getAgreedPrice(item) * item.total_qty,
    0
   ),
  [itemList]
 )

 const hasUnsavedPriceChanges = useMemo(() => {
  const hasItemChanges = itemList.some((item, index) => {
   const original = originalItemList[index]
   if (!original) return false
   return getAgreedPrice(item) !== getAgreedPrice(original)
  })
  return (
   hasItemChanges ||
   discountType !== originalDiscountType ||
   discountValue !== originalDiscountValue
  )
 }, [
  itemList,
  originalItemList,
  discountType,
  discountValue,
  originalDiscountType,
  originalDiscountValue,
 ])

 const validateNegotiatedPrices = (
  items: IItemDetails[]
 ) => {
  for (const item of items) {
   const agreedPrice = Number(getAgreedPrice(item))
   const minPrice = Number(item.max_negotiable_price)
   const maxPrice = Number(item.mrp)

   if (agreedPrice < minPrice || agreedPrice > maxPrice) {
    toast.error(
     `${
      item.product_name
     }: Price should be between ${formatNumberForDisplay(
      minPrice
     )} and ${formatNumberForDisplay(maxPrice)}`
    )
    return false
   }
  }

  return true
 }

 const amountToBePaid = useMemo(() => {
  const payable =
   amountDetails?.payment_details?.amount_payable
  if (payable != null && payable !== '') {
   return Number(payable)
  }
  if (
   orderDetails?.amount_to_be_paid != null &&
   !hasUnsavedPriceChanges
  ) {
   return orderDetails.amount_to_be_paid
  }
  if (discountType === 'PERCENTAGE') {
   return (
    computedOrderTotal -
    (computedOrderTotal * discountValue) / 100
   )
  }
  return computedOrderTotal - discountValue
 }, [
  amountDetails?.payment_details?.amount_payable,
  computedOrderTotal,
  discountType,
  discountValue,
  orderDetails?.amount_to_be_paid,
  hasUnsavedPriceChanges,
 ])

 const summaryOrderTotal = useMemo(() => {
  const total = amountDetails?.cart_details?.order_total
  if (total != null && total !== '') {
   return Number(total)
  }
  return computedOrderTotal
 }, [
  amountDetails?.cart_details?.order_total,
  computedOrderTotal,
 ])

 const getChangedItems = useCallback(
  (items: IItemDetails[]) =>
   items.filter((item) => {
    const original = originalItemList.find(
     (o) => o.sku_id === item.sku_id
    )
    return (
     original &&
     Number(getAgreedPrice(item)) !==
      Number(getAgreedPrice(original))
    )
   }),
  [originalItemList]
 )

 const applyOrderDiscountFromResponse = useCallback(
  (orderDiscount?: IOrderDiscountDetails) => {
   if (!orderDiscount) return

   const type =
    orderDiscount.discount_type === 'PERCENTAGE'
     ? 'PERCENTAGE'
     : 'FLAT'
   const value = Number(orderDiscount.discount_value ?? 0)
   const amount = Number(
    orderDiscount.discount_amount ?? value
   )

   setDiscountType(type)
   setDiscountValue(isNaN(value) ? 0 : value)
   setDiscountAmount(isNaN(amount) ? 0 : amount)
  },
  []
 )

 const buildOrderDiscount = useCallback(
  (type: DiscountType, value: number) => {
   if (
    value > 0 ||
    type !== originalDiscountType ||
    value !== originalDiscountValue
   ) {
    return {
     reference: 'custom',
     type,
     value: String(value),
    }
   }
   return undefined
  },
  [originalDiscountType, originalDiscountValue]
 )

 const buildCounterPayload = useCallback(
  (
   items: IItemDetails[],
   type: DiscountType,
   value: number
  ): ICounterOrderPayload => {
   const changedItems = getChangedItems(items)
   const payload: ICounterOrderPayload = {
    items: changedItems.map((item) => ({
     product_variant_id: item.sku_id,
     unit_price: Number(getAgreedPrice(item)).toFixed(2),
    })),
   }
   const orderDiscount = buildOrderDiscount(type, value)
   if (orderDiscount) {
    payload.order_discount = orderDiscount
   }
   return payload
  },
  [getChangedItems, buildOrderDiscount]
 )

 const fetchCounterPreview = useCallback(
  async (
   items: IItemDetails[],
   type: DiscountType,
   value: number
  ) => {
   const orderId = props.selectedOrder?.orderId
   if (!orderId) return

   const payload = buildCounterPayload(items, type, value)
   if (
    payload.items.length === 0 &&
    !payload.order_discount
   ) {
    return
   }

   try {
    setLoader(true)
    const res = await previewOrderCounter(
     orderId.toString(),
     payload
    )
    if (res?.amount_details) {
     setAmountDetails(res.amount_details)
    }
    if (res?.order_discount) {
     applyOrderDiscountFromResponse(res.order_discount)
    } else {
     setDiscountType(type)
     setDiscountValue(value)
     const breakupAmount =
      res?.amount_details?.cart_details?.cart_breakup?.find(
       (row) => row.key === 'discount'
      )?.value
     if (breakupAmount != null && breakupAmount !== '') {
      setDiscountAmount(Number(breakupAmount))
     } else if (type === 'FLAT') {
      setDiscountAmount(value)
     }
    }
   } finally {
    setLoader(false)
   }
  },
  [
   props.selectedOrder?.orderId,
   buildCounterPayload,
   applyOrderDiscountFromResponse,
  ]
 )

 const handleSaveAgreedPrice = useCallback(
  (skuId: number, newPrice: number) => {
   const nextItems = itemList.map((item) =>
    item.sku_id === skuId
     ? {...item, agreed_price: newPrice}
     : item
   )

   setItemList(nextItems)
   setEditingItemId(null)
   setEditingPriceDraft('')
   void fetchCounterPreview(
    nextItems,
    discountType,
    discountValue
   )
  },
  [
   itemList,
   discountType,
   discountValue,
   fetchCounterPreview,
  ]
 )

 const handleStartEditPrice = useCallback(
  (skuId: number, currentPrice: number) => {
   setEditingItemId(skuId)
   setEditingPriceDraft(currentPrice.toString())
  },
  []
 )

 const handleCancelEditPrice = useCallback(() => {
  setEditingItemId(null)
  setEditingPriceDraft('')
 }, [])

 const handleOpenEditDiscount = useCallback(() => {
  setShowEditDiscountModal(true)
 }, [])

 const handleCloseEditDiscount = useCallback(() => {
  setShowEditDiscountModal(false)
 }, [])

 const handleSaveDiscount = useCallback(
  (type: DiscountType, value: number) => {
   setDiscountType(type)
   setDiscountValue(value)
   if (type === 'FLAT') {
    setDiscountAmount(value)
   }
   setShowEditDiscountModal(false)
   void fetchCounterPreview(itemList, type, value)
  },
  [itemList, fetchCounterPreview]
 )

 const handleCellSave = useCallback(() => {
  if (editingItemId === null) return

  const parsedDraft = parseFloat(editingPriceDraft)

  if (isNaN(parsedDraft) || parsedDraft < 0) {
   toast.error('Please enter a valid price')
   return
  }

  const item = itemList.find(
   (i) => i.sku_id === editingItemId
  )

  if (!item) return

  const minPrice = Number(item.max_negotiable_price)
  const maxPrice = Number(item.mrp)

  if (parsedDraft < minPrice || parsedDraft > maxPrice) {
   toast.error(
    `Price should be between ${formatNumberForDisplay(
     minPrice
    )} and ${formatNumberForDisplay(maxPrice)}`
   )
   return
  }

  handleSaveAgreedPrice(editingItemId, parsedDraft)
 }, [
  editingItemId,
  editingPriceDraft,
  itemList,
  handleSaveAgreedPrice,
 ])
 const getOrderDetails = async () => {
  if (!props.selectedOrder?.orderId) return
  setLoader(true)
  const res = await fetchOrderDetails(
   props.selectedOrder.orderId.toString(),
   {}
  )
  if (res) {
   const items = normalizeItems(
    res?.order_items?.[0]?.items ?? []
   )
   reset({
    ...res,
    order_date: new Date(
     convertEpochToDate(res?.order_date)
    ),
   })
   setItemList(items)
   setOriginalItemList(JSON.parse(JSON.stringify(items)))
   if (res?.order_discount) {
    const type =
     res.order_discount.discount_type === 'PERCENTAGE'
      ? 'PERCENTAGE'
      : 'FLAT'
    const value = Number(
     res.order_discount.discount_value ??
      res?.discount_applied ??
      0
    )
    const amount = Number(
     res.order_discount.discount_amount ??
      res?.discount_applied ??
      value
    )
    setDiscountType(type)
    setOriginalDiscountType(type)
    setDiscountValue(isNaN(value) ? 0 : value)
    setOriginalDiscountValue(isNaN(value) ? 0 : value)
    setDiscountAmount(isNaN(amount) ? 0 : amount)
   } else {
    const discount = res?.discount_applied ?? 0
    setDiscountType('FLAT')
    setOriginalDiscountType('FLAT')
    setDiscountValue(discount)
    setOriginalDiscountValue(discount)
    setDiscountAmount(discount)
   }
   if (res.amount_details) {
    setAmountDetails(res.amount_details)
   } else {
    setAmountDetails(null)
   }
   setOrderDetails({
    ...res,
    original_amount_to_be_paid:
     res.amount_details?.payment_details?.amount_payable,
    is_negotiated:
     res.is_negotiated ??
     props.selectedOrder?.isNegotiated ??
     isNegotiatedOrder(res),
    negotiation_status:
     res.negotiation_status ??
     props.selectedOrder?.negotiationStatus,
    negotiation_info: res.negotiation_info,
   })
   setEditingItemId(null)
   setEditingPriceDraft('')
   setShowEditDiscountModal(false)
  }
  setLoader(false)
 }

 const handleSendForConfirmation = async () => {
  const orderId = props.selectedOrder?.orderId

  if (!orderId) return

  let itemsForPayload = itemList

  if (editingItemId !== null) {
   const parsedDraft = parseFloat(editingPriceDraft)

   if (!isNaN(parsedDraft) && parsedDraft >= 0) {
    itemsForPayload = itemList.map((item) =>
     item.sku_id === editingItemId
      ? {
         ...item,
         agreed_price: parsedDraft,
        }
      : item
    )

    setItemList(itemsForPayload)
    setEditingItemId(null)
    setEditingPriceDraft('')
   }
  }
  if (!validateNegotiatedPrices(itemsForPayload)) {
   setLoader(false)
   return
  }

  const payload = buildCounterPayload(
   itemsForPayload,
   discountType,
   discountValue
  )

  if (
   payload.items.length === 0 &&
   !payload.order_discount
  ) {
   return
  }

  try {
   setLoader(true)

   const res = await sendOrderForConfirmation(
    orderId.toString(),
    payload
   )

   if (res) {
    toast.success(res)
    navigate(-1)
    await getOrderDetails()
   }
  } finally {
   setLoader(false)
  }
 }

 useEffect(() => {
  setActionsAllowed(props.selectedOrder?.actionsAllowed)
  void getOrderDetails()
 }, [props.selectedOrder])

 useEffect(() => {
  if (actionsAllowed === undefined) {
   if (isPMU) setActionsAllowed(false)
   else if (isDistributorDirector) setActionsAllowed(true)
  }
 }, [actionsAllowed, isPMU, isDistributorDirector])

 const isNegotiated = isNegotiatedOrder(orderDetails ?? {})
 const negotiationStatus =
  orderDetails?.negotiation_status ??
  props.selectedOrder?.negotiationStatus

 const canPerformActions =
  actionsAllowed ?? props.selectedOrder?.actionsAllowed

 const acceptDisabled =
  canPerformActions === false ||
  (isNegotiated &&
   negotiationStatus === 'PENDING_BUYER_REVIEW')

 const showNegotiationBanner =
  showDistributorNegotiationUI &&
  isNegotiated &&
  negotiationStatus === 'PENDING_SELLER_REVIEW'
 const discountEditable =
  showDistributorNegotiationUI &&
  Boolean(negotiationStatus) &&
  !acceptDisabled

 const displayFormFields =
  showDistributorNegotiationUI && isNegotiated
   ? formFields.filter((f) => f.label_key !== 'order_total')
   : formFields

 const hasPriceChanges = useMemo(() => {
  const hasItemChanges = itemList.some((item, index) => {
   const original = originalItemList[index]

   return (
    original &&
    Number(getAgreedPrice(item)) !==
     Number(getAgreedPrice(original))
   )
  })

  return (
   hasItemChanges ||
   discountType !== originalDiscountType ||
   discountValue !== originalDiscountValue
  )
 }, [
  itemList,
  originalItemList,
  discountType,
  discountValue,
  originalDiscountType,
  originalDiscountValue,
 ])

 const itemTableColList: Array<ColumnProps<IItemDetails>> =
  useMemo(() => {
   const baseColumns: Array<ColumnProps<IItemDetails>> = [
    {
     key: 'srNo',
     title: t(
      'orderManagement.orderDetails.itemsTable.srNo'
     ),
     className: 'w-[80px] text-center',
     isSrNo: true,
    },
    {
     key: 'sku_id',
     title: t(
      'orderManagement.orderDetails.itemsTable.skuId'
     ),
    },
    {
     key: 'product_name',
     title: t(
      'orderManagement.orderDetails.itemsTable.productName'
     ),
    },
    {
     key: 'product_category',
     title: t(
      'orderManagement.orderDetails.itemsTable.productCategory'
     ),
    },
    {
     key: 'sku',
     title: t(
      'orderManagement.orderDetails.itemsTable.sku'
     ),
    },
    {
     key: 'total_qty',
     title: t(
      'orderManagement.orderDetails.itemsTable.orderQty'
     ),
     render: (_, record) => `${record.total_qty} units`,
    },
   ]

   if (showDistributorNegotiationUI) {
    const negotiationColumns: Array<
     ColumnProps<IItemDetails>
    > = [
     {
      key: 'catalogue_price',
      title: t('orderManagement.cataloguePrice'),
      render: (_, record) => {
       return record.seller_catalogue_unit_price
      },
     },
    ]

    if (negotiationStatus) {
     negotiationColumns.push({
      key: 'agreed_price',
      title: t('orderManagement.agreedPrice'),
      render: (_, record) => {
       return (
        <div className='bg-[#FFFEEB] h-full w-full flex items-center justify-center py-4 px-4'>
         <AgreedPriceCell
          agreedPrice={getAgreedPrice(record)}
          originalPrice={getCataloguePrice(record)}
          customerQuotedUnitPrice={
           record.price_quotes?.customer_quoted_unit_price
          }
          isEditing={editingItemId === record.sku_id}
          draftValue={
           editingItemId === record.sku_id
            ? editingPriceDraft
            : getAgreedPrice(record).toString()
          }
          editable={!acceptDisabled}
          onStartEdit={() =>
           handleStartEditPrice(
            record.sku_id,
            getAgreedPrice(record)
           )
          }
          onDraftChange={setEditingPriceDraft}
          onSave={handleCellSave}
          onCancel={handleCancelEditPrice}
         />
        </div>
       )
      },
     })
    }

    negotiationColumns.push({
     key: 'subTotal',
     title: t(
      'orderManagement.orderDetails.itemsTable.subTotal'
     ),
     render: (_, record) =>
      formatNumberForDisplay(
       Number(getAgreedPrice(record)) *
        Number(record.total_qty)
      ),
    })

    return [...baseColumns, ...negotiationColumns]
   }

   return [
    ...baseColumns,
    {
     key: 'price_unit',
     title: t(
      'orderManagement.orderDetails.itemsTable.unitPrice'
     ),
     render: (_, record) => record.price_unit,
    },
    {
     key: 'subTotal',
     title: t(
      'orderManagement.orderDetails.itemsTable.subTotal'
     ),
     render: (_, record) =>
      formatNumberForDisplay(
       Number(getAgreedPrice(record)) *
        Number(record.total_qty)
      ),
    },
   ]
  }, [
   t,
   showDistributorNegotiationUI,
   negotiationStatus,
   editingItemId,
   editingPriceDraft,
   actionsAllowed,
   acceptDisabled,
   handleStartEditPrice,
   handleCancelEditPrice,
   handleCellSave,
  ])

 const handleDownloadInvoice = async () => {
  if (orderDetails?.invoice_id) {
   setLoader(true)
   const res = await downloadInvoice(
    orderDetails?.invoice_id
   )
   if (res) {
    const filename = `Invoice_${orderDetails.order_number}.pdf`
    await downloadFileFromUrl(res, filename)
   }
   setLoader(false)
  } else {
   toast.error('Invoice is not available for this order')
  }
 }
 const renderOpenOrderActions = () => {
  const orderId = props.selectedOrder?.orderId
  const canPerformActions =
   actionsAllowed ?? props.selectedOrder?.actionsAllowed

  if (!orderId) return null

  const rejectBtnClass =
   'text-center py-[9px] px-6 bg-[#CC293F] !rounded-lg text-base text-white font-semibold border border-[#CC293F] mr-3'
  const acceptBtnClass =
   'text-center py-[9px] px-6 bg-[#0E7A41] !rounded-lg text-base text-white font-semibold border border-[#0E7A41] mr-3'
  const sendBtnClass =
   'text-center py-[9px] px-6 bg-[#4E83E4] !rounded-lg text-base text-white font-semibold border border-[#4E83E4]'
  const disabledBtnClass =
   ' opacity-50 cursor-not-allowed pointer-events-none'

  if (!showDistributorNegotiationUI) {
   return (
    <>
     <Button
      type='button'
      disabled={canPerformActions === false}
      onClick={() =>
       props.handleActionBtnClick({
        orderId,
        status: 'REJECTED',
       })
      }
      className={`${rejectBtnClass} hover:bg-white hover:text-[#CC293F]`}
     >
      {t('orderManagement.orderDetails.rejectOrder')}
     </Button>
     <Button
      type='button'
      disabled={canPerformActions === false}
      onClick={() =>
       props.handleActionBtnClick({
        orderId,
        status: 'CONFIRMED',
       })
      }
      className={`${acceptBtnClass} hover:bg-white hover:text-[#0E7A41]`}
     >
      {t('orderManagement.orderDetails.acceptOrder')}
     </Button>
    </>
   )
  }

  if (isEditing) {
   return (
    <>
     <Button
      type='button'
      disabled
      className={`${rejectBtnClass}${disabledBtnClass}`}
     >
      {t('orderManagement.orderDetails.rejectOrder')}
     </Button>

     <Button
      type='button'
      disabled
      className={`${acceptBtnClass}${disabledBtnClass}`}
     >
      {t('orderManagement.orderDetails.acceptOrder')}
     </Button>
    </>
   )
  }
  if (hasPriceChanges) {
   return (
    <>
     <Button
      type='button'
      disabled={canPerformActions === false}
      onClick={() =>
       props.handleActionBtnClick({
        orderId,
        status: 'REJECTED',
       })
      }
      className={`${rejectBtnClass} hover:bg-white hover:text-[#CC293F]`}
     >
      {t('orderManagement.orderDetails.rejectOrder')}
     </Button>
     <Button
      type='button'
      onClick={() => {
       void handleSendForConfirmation()
      }}
      className={`${sendBtnClass} hover:bg-white hover:text-[#4E83E4]`}
     >
      {t('orderManagement.sendForConfirmation')}
     </Button>
    </>
   )
  }

  return (
   <>
    <Button
     type='button'
     disabled={canPerformActions === false}
     onClick={() =>
      props.handleActionBtnClick({
       orderId,
       status: 'REJECTED',
      })
     }
     className={`${rejectBtnClass} hover:bg-white hover:text-[#CC293F]`}
    >
     {t('orderManagement.orderDetails.rejectOrder')}
    </Button>
    <Button
     type='button'
     disabled={acceptDisabled}
     onClick={() =>
      props.handleActionBtnClick({
       orderId,
       status: 'CONFIRMED',
      })
     }
     className={`${acceptBtnClass} hover:bg-white hover:text-[#0E7A41] disabled:opacity-50`}
    >
     {t('orderManagement.orderDetails.acceptOrder')}
    </Button>
   </>
  )
 }

 const renderActionButtons = () => {
  const {selectedOrder, handleActionBtnClick} = props

  const orderId = selectedOrder?.orderId
  const canPerformActions =
   actionsAllowed ?? selectedOrder?.actionsAllowed
  if (!orderId) return null

  switch (actionTabId) {
   case 'open_orders':
    return renderOpenOrderActions()

   case 'accepted_orders':
    return (
     <>
      <Button
       type='button'
       disabled={canPerformActions === false}
       onClick={() =>
        handleActionBtnClick({
         orderId,
         status: 'CANCELLED',
        })
       }
       className='text-center py-[9px] px-6 bg-[#CC293F] text-white rounded-lg border-[#CC293F] mr-2 
       hover:bg-white hover:text-[#CC293F]'
      >
       {t('orderManagement.tableCol.cancel')}
      </Button>
      <Button
       type='button'
       disabled={!canPerformActions}
       onClick={() =>
        handleActionBtnClick({
         orderId,
         status: 'SHIPPED',
        })
       }
       className='text-center py-[9px] px-6 bg-[#4E83E4] text-white rounded-lg border-[#4E83E4] mr-2 
       hover:bg-white hover:text-[#4E83E4]'
      >
       {t('orderManagement.tableCol.markDispatch')}
      </Button>
     </>
    )

   case 'dispatched_orders':
    return (
     <>
      <Button
       type='button'
       disabled={false}
       onClick={handleDownloadInvoice}
       className='mr-2 text-center py-[9px] px-6 bg-white text-[#4E83E4] rounded-lg border-[#E6EBF0] text-base font-semibold
       hover:bg-[#4E83E4] hover:text-white'
      >
       <i className='fa fa-eye mr-2' aria-hidden='true'></i>
       <span className='underline'>
        {t('orderManagement.viewInvoice')}
       </span>
      </Button>
      {orderDetails?.order_status === 'SHIPPED' ? (
       <Button
        type='button'
        disabled={canPerformActions === false}
        onClick={() =>
         handleActionBtnClick({
          ...orderDetails,
          orderId,
          status: 'DELIVERED',
         })
        }
        className='text-center py-[9px] px-6 bg-[#4E83E4] text-white rounded-lg border-[#4E83E4] 
       hover:bg-white hover:text-[#4E83E4]'
       >
        {t('orderManagement.markDelivery')}
       </Button>
      ) : (
       <button
        type='button'
        disabled={true}
        color='blue'
        className={`text-center py-[9px] px-6 !w-[92px] !bg-[#F1FFEB] rounded-sm text-xs !border-[#43A314] text-[#43A314] font-semibold 
            disabled:text-[#43A314]`}
       >
        {t('orderManagement.delivered')}
       </button>
      )}
     </>
    )

   case 'pending_payment_orders':
    return (
     <>
      <Button
       type='button'
       disabled={canPerformActions === false}
       onClick={() =>
        handleActionBtnClick({
         orderId,
         status: 'PAYMENT_CONFIRMED',
        })
       }
       className='text-center py-[9px] px-6 bg-[#4E83E4] text-white rounded-lg border-[#4E83E4] mr-2 
       hover:bg-white hover:text-[#4E83E4]'
      >
       {t('orderManagement.confirm')}
      </Button>
      {/* <Button
       type='button'
       onClick={() =>
        handleActionBtnClick({
         orderId,
         status: 'PAYMENT_CONFIRMED',
        })
       }
       className='text-center py-[9px] px-6 bg-[#4E83E4] text-white rounded-lg border-[#4E83E4] 
       hover:bg-white hover:text-[#4E83E4]'
      >
       {t('orderManagement.request')}
      </Button> */}
     </>
    )

   default:
    return null
  }
 }

 return (
  <>
   {loader && <Spinner />}
   <div className='w-full relative pb-28'>
    <PageTitle
     breadcrumbContainerClass={
      'flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0] border-b'
     }
     breadcrumbs={breadcrumbs}
     description='Welcome'
    >
     Order Details
    </PageTitle>
    <form>
     <div className='grid md:grid-cols-3 gap-5 w-full mt-6 border-b border-[#E6EBF0] pb-6 mb-6'>
      <GenericFormBuilder
       isDisabled={true}
       itemConfigList={[
        {
         item_identifier: 'order_details',
         fields: displayFormFields,
        },
       ]}
       excludedFields={[]}
       itemIdentifier='order_details'
       setLoader={setLoader}
       register={register}
       unregister={unregister}
       control={control}
       getValues={getValues}
       errors={errors}
       watch={watch}
       setValue={setValue}
       className=''
      />
      {showDistributorNegotiationUI && isNegotiated && (
       <div>
        <label className='text-[#5A6066] text-sm font-normal mb-1 block'>
         {t('orderManagement.orderDetails.orderValue')}
        </label>
        <div className='flex items-stretch rounded-lg border border-[#E6EBF0] bg-white overflow-hidden'>
         <div className='bg-[#E6EBF0] px-4 flex items-center text-[#33393D] text-2xl font-medium'>
          <i className='fa fa-inr' aria-hidden='true' />
         </div>
         <div className='flex items-center gap-2 px-4 py-3 flex-1'>
          {orderDetails?.seller_catalogue_total != null &&
           Number(orderDetails.seller_catalogue_total) !==
            computedOrderTotal && (
            <span className='line-through text-[#80868C] text-lg font-medium'>
             {formatNumberForDisplay(
              orderDetails.seller_catalogue_total
             )}
            </span>
           )}
          <span className='text-[#33393D] text-lg font-semibold'>
           {formatNumberForDisplay(computedOrderTotal)}
          </span>
         </div>
        </div>
       </div>
      )}
     </div>
    </form>

    <NegotiationBanner
     visible={showNegotiationBanner}
     data={orderDetails?.negotiation_info}
    />

    <div className='w-full'>
     <KTTable
      data={itemList}
      columns={itemTableColList}
      headerClass='bg-[#80868C] border border-[#80868C]'
      headerColClass='!border-0 text-left'
      colClass='text-[#33393D] !text-sm font-normal !border-0'
      tableClass='border  border border-[#E6EBF0]'
      rowClass='even:bg-[#F5F8FA]'
      showPagination={false}
     />
    </div>

    {showOrderSummary && (
     <OrderTotalsSummary
      cartBreakup={
       amountDetails?.cart_details?.cart_breakup
      }
      orderTotal={summaryOrderTotal}
      orderTotalBeforeDiscount={
       amountDetails?.cart_details
        ?.order_total_before_discount != null &&
       amountDetails.cart_details
        .order_total_before_discount !== ''
        ? Number(
           amountDetails.cart_details
            .order_total_before_discount
          )
        : summaryOrderTotal
      }
      amountPayable={amountToBePaid}
      sellerCatalogueTotal={
       orderDetails?.seller_catalogue_total
      }
      originalAmountToBePaid={Number(
       orderDetails?.original_amount_to_be_paid
      )}
      showStrikethrough={isNegotiatedOrder(
       orderDetails ?? {}
      )}
      paymentMode={orderDetails?.payment_mode}
      discountType={discountType}
      discountValue={discountValue}
      discountAmount={discountAmount}
      discountEditable={discountEditable}
      onEditDiscount={handleOpenEditDiscount}
     />
    )}

    <EditDiscountModal
     show={showEditDiscountModal}
     discountType={discountType}
     discountValue={discountValue}
     aggregatorDiscountAmount={discountAmount}
     onClose={handleCloseEditDiscount}
     onSave={handleSaveDiscount}
    />

    <div
     className='fixed bottom-0 left-0 bg-white w-full z-10'
     style={{
      boxShadow: '0px -2px 6px 0px rgba(0, 0, 0, 0.16)',
     }}
    >
     <div className='container flex items-center justify-end py-6 px-[76px]'>
      {renderActionButtons()}
     </div>
    </div>
   </div>
  </>
 )
}

export default OrderDetails
